<template>
  <v-card :loading="loading">
    <!-- 編輯模式 Type 1 & 2：顯示患者資訊區 -->
    <v-card-title v-if="editMode && type !== 3" style="max-height: 180px; overflow-x: auto">
      <v-list-item three-line class="px-1" style="max-width: 100%;">
        <v-list-item-avatar :size="$vuetify.breakpoint.xsOnly ? 60 : 80" color="grey">
          <v-img :src="initialData.file_name_photo"/>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="text-h5 mb-1">
            {{ initialData.name }}
          </v-list-item-title>
          <v-list-item-subtitle class="d-flex">
            <div v-for="(item, i) in info" :key="i" class="d-flex align-center mr-2">
              <v-img
                v-if="item.isImage"
                :src="item.icon"
                max-width="16"
                width="16"
                class="mr-1"
              />
              <v-icon v-else class="mr-1" size="16">
                {{ item.icon }}
              </v-icon>
              <div>
                {{ item.value }}
              </div>
            </div>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-card-title>

    <!-- 新增模式 或 Type 3：普通標題 -->
    <v-card-title v-else>
      {{ typeTitle }}
    </v-card-title>

    <v-divider v-if="editMode && type !== 3"></v-divider>

    <v-card-text>
      <v-form ref="form" v-model="valid">
        <!-- 備註類型：簡化表單 -->
        <template v-if="type === 3">
          <!-- 日期選擇 -->
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formData.date"
                label="日期"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="[v => !!v || '請選擇日期']"
                hide-details="auto"
                class="mb-3"
                append-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="formData.date"
              @input="dateMenu = false"
              locale="zh-TW"
            ></v-date-picker>
          </v-menu>
          <!-- 醫師選擇（診所登入時，非必填） -->
          <v-select
            v-if="clinicUser"
            v-model="formData.doctor_code"
            :items="doctorList"
            item-text="doctor_name"
            item-value="doctor_code"
            label="選擇醫師（選填）"
            outlined
            dense
            :loading="loadingList"
            :disabled="loadingList"
            hide-details="auto"
            class="mb-3"
            clearable
          ></v-select>

          <!-- 診所選擇（醫師登入時，非必填） -->
          <v-select
            v-if="doctorUser"
            v-model="formData.clinic_code"
            :items="clinicList"
            item-text="clinic_name"
            item-value="clinic_code"
            label="選擇診所（選填）"
            outlined
            dense
            :loading="loadingList"
            :disabled="loadingList"
            hide-details="auto"
            class="mb-3"
            clearable
          ></v-select>

          <v-textarea
            v-model="formData.note"
            label="備註內容"
            outlined
            dense
            rows="3"
            :rules="[v => !!v || '請輸入備註內容']"
            hide-details="auto"
            class="mb-3"
          ></v-textarea>
        </template>

        <!-- 定檢/額外提醒類型：完整表單 -->
        <template v-else>
          <!-- 第一排：新增模式選擇一般或NP約診 -->
          <v-radio-group
            v-if="!editMode"
            v-model="patientMode"
            row
            hide-details="auto"
            class="mb-3 mt-0"
          >
            <v-radio label="一般約診" value="normal"></v-radio>
            <v-radio label="NP約診" value="np"></v-radio>
          </v-radio-group>

          <!-- 第二排：病患資訊 -->
          <!-- 一般約診：患者搜尋 -->
          <search-patient
            v-if="!editMode && patientMode === 'normal'"
            :patient.sync="selectedPatient"
            @update:patient="handlePatientSelect"
            @no-data="useNP"
            class="mb-3"
          />

          <!-- NP約診：輸入新病患資料 -->
          <template v-if="!editMode && patientMode === 'np'">
            <v-text-field
              v-model.trim="npPatientName"
              label="病患姓名"
              outlined
              dense
              :rules="[v => !!v || '請輸入病患姓名']"
              hide-details="auto"
              class="mb-3"
            ></v-text-field>
            <v-text-field
              v-model.trim="npPatientPhone"
              label="病患電話"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
            ></v-text-field>
            <v-text-field
              v-model.trim="npPatientBirthday"
              label="病患生日（民國年月日，例如：0900101）"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
              placeholder="0900101"
            ></v-text-field>
          </template>

          <!-- 第三排：日期選擇 -->
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formData.date"
                label="日期"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="[v => !!v || '請選擇日期']"
                hide-details="auto"
                class="mb-3"
                append-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="formData.date"
              @input="dateMenu = false"
              locale="zh-TW"
            ></v-date-picker>
          </v-menu>

          <!-- 發送時間顯示（Type 1 & 2） -->
          <div v-if="sendTime" class="text-body-1 mb-3">
            <span class="grey--text text--darken-2">發送時間：</span>
            <span class="black--text font-weight-medium">{{ sendTime }}</span>
            <span class="grey--text text--darken-1 ml-2">(固定時間)</span>
          </div>

          <!-- 定檢類型選擇（僅 type 1 顯示） -->
          <v-sheet
            v-if="type === 1"
            outlined
            class="px-3 py-2 rounded mb-3"
            style="border-color: rgba(0,0,0,.38);"
          >
            <v-radio-group
              v-model="formData.checkupType"
              :rules="[v => !!v || '請選擇定檢類型']"
              hide-details="auto"
              class="mt-0"
              row
            >
              <template v-slot:label>
                <div class="text-body-2 grey--text text--darken-2">定檢類型</div>
              </template>
              <v-radio label="3個月定檢" value="3M"></v-radio>
              <v-radio label="6個月定檢" value="6M"></v-radio>
            </v-radio-group>
          </v-sheet>

          <!-- 醫師選擇（診所登入時） -->
          <v-select
            v-if="clinicUser"
            v-model="formData.doctor_code"
            :items="doctorList"
            item-text="doctor_name"
            item-value="doctor_code"
            label="選擇醫師"
            outlined
            dense
            :loading="loadingList"
            :disabled="loadingList"
            :rules="[v => !!v || '請選擇醫師']"
            hide-details="auto"
            class="mb-3"
          ></v-select>

          <!-- 診所選擇（醫師登入時） -->
          <v-select
            v-if="doctorUser"
            v-model="formData.clinic_code"
            :items="clinicList"
            item-text="clinic_name"
            item-value="clinic_code"
            label="選擇診所"
            outlined
            dense
            :loading="loadingList"
            :disabled="loadingList"
            :rules="[v => !!v || '請選擇診所']"
            hide-details="auto"
            class="mb-3"
          ></v-select>

          <!-- 額外提醒 -->
          <v-textarea
            v-model="formData.remind_others"
            label="額外提醒"
            outlined
            dense
            rows="2"
            hide-details="auto"
            class="mb-3"
          ></v-textarea>

          <!-- 診所內備註 -->
          <v-textarea
            v-model="formData.note"
            label="診所內備註"
            outlined
            dense
            rows="2"
            hide-details="auto"
            class="mb-3"
          ></v-textarea>
        </template>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('cancel')" :disabled="loading">取消</v-btn>
      <v-btn color="primary" :disabled="!valid || loading" :loading="loading" @click="submit">確定</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { createReserveSpecifyAPI, updateReserveSpecifyAPI, getDoctorListAPI, getClinicListAPI } from '@/api';
import { mapGetters } from 'vuex';
import SearchPatient from './SearchPatient.vue';
import { formatDate, formatROCDate, assetURL } from '@/utils/filters';
import { forceTaiwanTimestamp } from '@/utils/timezoneHelper';
import { bindStatus } from '@/config/bindConfig';

// 發送時間配置（未來可擴展為可編輯的下拉選單）
const SEND_TIME_CONFIG = {
  1: '09:00',  // 定檢 6M
  2: '09:10',  // 額外提醒
  4: '09:00',  // 定檢 3M
  // Type 3（備註）不需要發送時間
};

export default {
  name: 'AllDayEventForm',
  components: {
    SearchPatient
  },
  props: {
    type: {
      type: Number,
      required: true,
      validator: v => [1, 2, 3].includes(v)
    },
    date: {
      type: String,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      valid: false,
      loading: false,
      selectedPatient: null,
      patientMode: 'normal', // 一般約診 or NP約診
      dateMenu: false, // 日期選擇器 menu
      // NP 約診資料
      npPatientName: '',
      npPatientPhone: '',
      npPatientBirthday: '',
      formData: {
        patient_code: null,
        doctor_code: null,
        clinic_code: null,
        date: '', // 日期（可編輯）
        checkupType: '6M', // 定檢類型：3M 或 6M
        remind_others: '',
        note: '',
      },
      // 醫師/診所選項列表
      doctorList: [],
      clinicList: [],
      loadingList: false
    };
  },
  computed: {
    ...mapGetters(['axiosParamData', 'clinicUser', 'doctorUser']),
    typeTitle() {
      const prefix = this.editMode ? '編輯' : '新增';
      const titles = {
        1: `${prefix}定檢提醒（${this.formData.checkupType}）`,
        2: `${prefix}額外提醒`,
        3: `${prefix}備註`
      };
      return titles[this.type];
    },
    // 是否需要選擇醫師/診所（type 1/2 必填，type 3 選填）
    needDoctorOrClinic() {
      return this.type === 1 || this.type === 2;
    },
    // 發送時間（根據 type 和 checkupType 決定）
    sendTime() {
      if (this.type === 3) return null; // 備註不需要發送時間

      if (this.type === 1) {
        // 定檢提醒：根據 checkupType 決定是 type 1 (6M) 或 type 4 (3M)
        const actualType = this.formData.checkupType === '3M' ? 4 : 1;
        return SEND_TIME_CONFIG[actualType];
      }

      return SEND_TIME_CONFIG[this.type];
    },
    info() {
      if (!this.editMode || !this.initialData) return [];

      const info = [];
      if (parseInt(this.initialData.sex) === 0) {
        info.push({ icon: 'mdi-gender-female', value: '女' });
      } else if (parseInt(this.initialData.sex) === 1) {
        info.push({ icon: 'mdi-gender-male', value: '男' });
      }

      if (this.initialData.birthday) {
        info.push({ icon: 'mdi-cake-variant', value: formatROCDate(forceTaiwanTimestamp(parseInt(this.initialData.birthday))) });
      }

      if (this.initialData.phone) {
        info.push({ icon: 'mdi-cellphone-iphone', value: this.initialData.phone });
      }

      // 綁定狀態
      if (this.initialData.bind_status !== undefined) {
        const assetConfig = bindStatus({ status: this.initialData.bind_status });
        info.push({
          icon: assetURL(assetConfig.icon),
          value: assetConfig.text,
          isImage: true
        });
      }

      return info;
    }
  },
  mounted() {
    // 所有類型都加載醫師/診所列表（Type 3 為選填，Type 1 & 2 為必填）
    this.loadDoctorOrClinicList();

    // 初始化日期
    this.formData.date = this.date;

    // 編輯模式：填充初始數據
    if (this.editMode && this.initialData) {
      // 根據 reserve_type 推導 checkupType
      let checkupType = '6M'; // 預設
      if (this.initialData.reserve_type === '4') {
        checkupType = '3M';
      } else if (this.initialData.reserve_type === '1') {
        checkupType = '6M';
      }

      // 處理日期：需要時區轉換
      let date = this.date;
      if (this.initialData.start_time) {
        date = formatDate(forceTaiwanTimestamp(this.initialData.start_time), 'YYYY-MM-DD');
      }

      this.formData = {
        patient_code: this.initialData.patient_code || null,
        doctor_code: this.initialData.doctor_code || null,
        clinic_code: this.initialData.clinic_code || null,
        date: date,
        checkupType: checkupType,
        remind_others: this.initialData.remind_others || '',
        note: this.initialData.note || '',
      };
    }
  },
  methods: {
    handlePatientSelect(patient) {
      if (patient) {
        this.formData.patient_code = patient.patient_code;
      } else {
        this.formData.patient_code = null;
      }
    },

    useNP() {
      // 當 SearchPatient 找不到病患時，自動切換到 NP 模式
      this.patientMode = 'np';
    },

    async loadDoctorOrClinicList() {
      this.loadingList = true;
      try {
        if (this.clinicUser) {
          // 診所登入：加載醫師列表
          const res = await getDoctorListAPI(this.axiosParamData);
          this.doctorList = res.data;
        } else if (this.doctorUser) {
          // 醫師登入：加載診所列表
          const res = await getClinicListAPI(this.axiosParamData);
          this.clinicList = res.data;
        }
      } catch (error) {
        console.error('加載醫師/診所列表失敗:', error);
      } finally {
        this.loadingList = false;
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        // 決定實際的 reserve_type
        let reserveType = String(this.type);
        if (this.type === 1) {
          // 定檢提醒：根據 checkupType 決定是 type 1 (6M) 或 type 4 (3M)
          reserveType = this.formData.checkupType === '3M' ? '4' : '1';
        }

        const payload = {
          ...this.axiosParamData,
          reserve_type: reserveType,
          start_time: this.formData.date,
          note: this.formData.note || null,
          remind_others: this.formData.remind_others || null,
        };

        // Type 1 & 2: 患者資訊必填，doctor_code/clinic_code 必填
        // Type 3: patient_code 不需要，doctor_code/clinic_code 選填
        if (this.type !== 3) {
          if (this.editMode || this.patientMode === 'normal') {
            // 編輯模式 或 一般約診：使用 patient_code
            payload.patient_code = this.formData.patient_code;
          } else if (this.patientMode === 'np') {
            // NP 約診：使用新病患資料
            payload.name = this.npPatientName;
            payload.phone = this.npPatientPhone || null;
            payload.birthday = this.npPatientBirthday || null;
          }
        }

        // 診所登入：需要指定 doctor_code（Type 1 & 2 必填，Type 3 選填）
        if (this.clinicUser && this.formData.doctor_code) {
          payload.doctor_code = this.formData.doctor_code;
        }
        // 醫師登入：需要指定 clinic_code（Type 1 & 2 必填，Type 3 選填）
        else if (this.doctorUser && this.formData.clinic_code) {
          payload.clinic_code = this.formData.clinic_code;
        }

        // 根據模式調用對應的 API
        if (this.editMode) {
          await updateReserveSpecifyAPI(this.initialData.reserve_no, payload);
        } else {
          await createReserveSpecifyAPI(payload);
        }

        this.$emit('success');
      } catch (error) {
        console.error(`${this.editMode ? '更新' : '創建'} all-day 事件失敗:`, error);
        this.$emit('error', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
